import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { useAuth } from "../../../context/AuthContext";
import { buscarPagoMasivo, guardarPagoMasivo, previewPagoMasivo } from "../services/pagoMasivoService";

const MESES = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Nacional"];
const TIPOS_INGRESO = [
  { value: "1", label: "Servicios" },
  { value: "2", label: "Franquicia" },
  { value: "3", label: "Intercambio Serv." },
  { value: "4", label: "Castigo Deuda" },
];

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 30, fontSize: "0.78rem", borderRadius: "4px" },
  "& .MuiInputBase-input": { py: 0.45 },
  "& .MuiInputLabel-root": { fontSize: "0.76rem", transform: "translate(14px, 7px) scale(1)" },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiSelect-select": { py: "5px" },
};

const now = new Date();
const money = (value) => Number(value || 0).toFixed(2);
const dataOf = (res) => res?.data?.data ?? res?.data ?? {};

const PagoMasivoPage = () => {
  const { user } = useAuth();
  const [filtros, setFiltros] = useState({
    mes: now.getMonth() + 1,
    year: now.getFullYear(),
    sucursal: user?.sucursal || "La Paz",
    cliente: "",
  });
  const [facturas, setFacturas] = useState([]);
  const [extractos, setExtractos] = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [selectedExtracto, setSelectedExtracto] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tipo_ingreso: "1",
    numcheque: "",
    numrecibo: "",
    obs: "",
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const totalSeleccionado = useMemo(() => {
    const set = new Set(selectedFacturas);
    return facturas.reduce((acc, f) => set.has(f.cod) ? acc + Number(f.saldo_moneda || f.saldo || 0) : acc, 0);
  }, [facturas, selectedFacturas]);

  const extractoSeleccionado = extractos.find((e) => String(e.id) === String(selectedExtracto));

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const res = await buscarPagoMasivo(filtros);
      const data = dataOf(res);
      setFacturas(Array.isArray(data.facturas) ? data.facturas : []);
      setExtractos(Array.isArray(data.extractos) ? data.extractos : []);
      setSelectedFacturas([]);
      setSelectedExtracto("");
      setPreview(null);
    } catch (e) {
      showToast(e?.response?.data?.message || "No se pudo buscar", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleFactura = (cod) => {
    setSelectedFacturas((prev) => prev.includes(cod)
      ? prev.filter((c) => c !== cod)
      : [...prev, cod]);
  };

  const quitarFactura = (cod) => {
    setSelectedFacturas((prev) => prev.filter((c) => c !== cod));
    setPreview((prev) => {
      if (!prev?.allocations) return prev;
      const allocations = prev.allocations.filter((a) => a.cod_factura !== cod);
      const totalPagoBs = allocations.reduce((acc, a) => acc + Number(a.monto_pago_bs || 0), 0);
      const saldoFacturasFinal = allocations.reduce((acc, a) => acc + Number(a.saldo_despues_bs || 0), 0);
      const extractoInicial = Number(prev.extracto?.saldo_disponible || 0);
      const divisor = prev.extracto?.pago_moneda === "dolares" ? Number(prev.extracto?.tipo_cambio_usado || 1) : 1;
      const saldoExtractoFinal = Math.max(0, extractoInicial - (totalPagoBs / divisor));
      return {
        ...prev,
        allocations,
        totales: {
          ...prev.totales,
          total_pago_bs: Number(totalPagoBs.toFixed(2)),
          saldo_extracto_final: Number(saldoExtractoFinal.toFixed(2)),
          saldo_facturas_final: Number(saldoFacturasFinal.toFixed(2)),
        },
      };
    });
  };

  const visualizar = async () => {
    if (!selectedFacturas.length) return showToast("Seleccione al menos una factura", "warning");
    if (!selectedExtracto) return showToast("Seleccione un extracto", "warning");
    setLoading(true);
    try {
      const res = await previewPagoMasivo({ id_extracto: selectedExtracto, facturas: selectedFacturas });
      setPreview(dataOf(res));
      setPreviewOpen(true);
    } catch (e) {
      showToast(e?.response?.data?.message || "No se pudo calcular el pago", "error");
    } finally {
      setLoading(false);
    }
  };

  const guardar = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await guardarPagoMasivo({
        id_extracto: selectedExtracto,
        facturas: selectedFacturas,
        tipo_ingreso: form.tipo_ingreso,
        numcheque: form.numcheque,
        numrecibo: form.numrecibo,
        obs: form.obs,
        cobrador: user?.username || "",
        sucursal_usuario: user?.sucursal || "",
      });
      showToast("Emparejamiento guardado");
      setPreviewOpen(false);
      await buscar();
    } catch (e) {
      showToast(e?.response?.data?.message || "No se pudo guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ width: "100%" }}>
        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
          <Grid container spacing={0.75} alignItems="center">
            <Grid item xs={6} md={1.4}>
              <TextField select fullWidth size="small" label="Mes" sx={compactFieldSx}
                value={filtros.mes} onChange={(e) => setFiltros((p) => ({ ...p, mes: e.target.value }))}>
                {MESES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={1.2}>
              <TextField fullWidth size="small" label="Año" sx={compactFieldSx}
                value={filtros.year} onChange={(e) => setFiltros((p) => ({ ...p, year: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1.8}>
              <TextField select fullWidth size="small" label="Sucursal" sx={compactFieldSx}
                value={filtros.sucursal} onChange={(e) => setFiltros((p) => ({ ...p, sucursal: e.target.value }))}>
                {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Cliente" sx={compactFieldSx}
                value={filtros.cliente} onChange={(e) => setFiltros((p) => ({ ...p, cliente: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1.4}>
              <Button fullWidth size="small" variant="contained" startIcon={<SearchIcon />} disableElevation
                sx={{ height: 30, fontSize: "0.75rem" }} onClick={buscar} disabled={loading}>
                Buscar
              </Button>
            </Grid>
            <Grid item xs={6} md={2.2}>
              <Button fullWidth size="small" color="success" variant="contained" startIcon={<VisibilityIcon />} disableElevation
                sx={{ height: 30, fontSize: "0.75rem" }} onClick={visualizar} disabled={loading || !selectedFacturas.length || !selectedExtracto}>
                Visualizar pago
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: "flex", gap: 0.75, mb: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`Facturas: ${facturas.length}`} />
          <Chip size="small" label={`Seleccionadas: ${selectedFacturas.length}`} />
          <Chip size="small" color="warning" label={`Saldo facturas: ${money(totalSeleccionado)}`} />
          <Chip size="small" color="primary" label={`Extracto: ${extractoSeleccionado ? money(extractoSeleccionado.saldo_disponible) : "0.00"}`} />
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 1, py: 0.75, bgcolor: "#f7fbff", borderBottom: "1px solid #e6eef6" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800 }}>Facturas o pre-facturas</Typography>
              </Box>
              <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { py: 0.5, px: 0.6, fontSize: "0.72rem" } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Cod</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Periodo</TableCell>
                      <TableCell>Moneda</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={6}><CircularProgress size={18} /></TableCell></TableRow>
                    ) : facturas.length ? facturas.map((f) => (
                      <TableRow key={f.cod} hover selected={selectedFacturas.includes(f.cod)} sx={{ "& td": { py: 0.35, px: 0.6, fontSize: "0.74rem" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selectedFacturas.includes(f.cod)} onChange={() => toggleFactura(f.cod)} />
                        </TableCell>
                        <TableCell>{f.cod}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.74rem", fontWeight: 700 }}>{f.cliente}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{f.servicio_descripcion || f.fac_Nombre}</Typography>
                        </TableCell>
                        <TableCell>{f.mes} {f.year}</TableCell>
                        <TableCell>{f.moneda_label}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "error.main" }}>{money(f.saldo_moneda || f.saldo)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary", fontSize: "0.78rem" }}>Busca para cargar facturas pendientes.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 1, py: 0.75, bgcolor: "#fff8e6", borderBottom: "1px solid #f0e4bf" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800 }}>Extracto bancario</Typography>
              </Box>
              <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { py: 0.5, px: 0.6, fontSize: "0.72rem" } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Cod</TableCell>
                      <TableCell>Banco</TableCell>
                      <TableCell>Descripcion</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Disponible</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {extractos.length ? extractos.map((e) => (
                      <TableRow key={e.id} hover selected={String(selectedExtracto) === String(e.id)} sx={{ "& td": { py: 0.35, px: 0.6, fontSize: "0.74rem" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={String(selectedExtracto) === String(e.id)} onChange={() => setSelectedExtracto(String(e.id))} />
                        </TableCell>
                        <TableCell>{e.id}</TableCell>
                        <TableCell>{e.banco}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.74rem" }}>{e.descripcion || "-"}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{e.adicional || e.cod_transaccion || "-"}</Typography>
                        </TableCell>
                        <TableCell>{String(e.fecha || "").slice(0, 10)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "error.main" }}>{money(e.saldo_disponible)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary", fontSize: "0.78rem" }}>Sin extractos para el filtro.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 800 }}>Realizar pago masivo</DialogTitle>
        <DialogContent sx={{ p: 1.25 }}>
          {preview && (
            <>
              <Grid container spacing={0.75} sx={{ mb: 1 }}>
                <Grid item xs={6} md={2}>
                  <TextField select fullWidth size="small" label="Tipo ingreso" sx={compactFieldSx}
                    value={form.tipo_ingreso} onChange={(e) => setForm((p) => ({ ...p, tipo_ingreso: e.target.value }))}>
                    {TIPOS_INGRESO.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" label="Nro cheque" sx={compactFieldSx}
                    value={form.numcheque} onChange={(e) => setForm((p) => ({ ...p, numcheque: e.target.value }))} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" label="Nro recibo" sx={compactFieldSx}
                    value={form.numrecibo} onChange={(e) => setForm((p) => ({ ...p, numrecibo: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Observacion" sx={compactFieldSx}
                    value={form.obs} onChange={(e) => setForm((p) => ({ ...p, obs: e.target.value }))} />
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
                <Chip size="small" color="warning" label={`Extracto disponible: ${money(preview.extracto?.saldo_disponible)} ${preview.extracto?.pago_moneda}`} />
                <Chip size="small" color="success" label={`Total pago Bs: ${money(preview.totales?.total_pago_bs)}`} />
                <Chip size="small" label={`Saldo extracto final: ${money(preview.totales?.saldo_extracto_final)}`} />
                <Chip size="small" label={`Saldo facturas final: ${money(preview.totales?.saldo_facturas_final)}`} />
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 430 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { py: 0.5, px: 0.6, fontSize: "0.72rem" } }}>
                      <TableCell>Cod</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Periodo</TableCell>
                      <TableCell align="right">Saldo actual Bs</TableCell>
                      <TableCell align="right">Pago Bs</TableCell>
                      <TableCell align="right">Saldo final Bs</TableCell>
                      <TableCell>Razon</TableCell>
                      <TableCell align="center">Quitar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.allocations.map((a) => (
                      <TableRow key={a.cod_factura} sx={{ "& td": { py: 0.35, px: 0.6, fontSize: "0.74rem" } }}>
                        <TableCell>{a.cod_factura}</TableCell>
                        <TableCell>{a.cliente}</TableCell>
                        <TableCell>{a.mes} {a.year}</TableCell>
                        <TableCell align="right">{money(a.saldo_actual_bs)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: a.monto_pago_bs > 0 ? "success.main" : "text.secondary" }}>{money(a.monto_pago_bs)}</TableCell>
                        <TableCell align="right">{money(a.saldo_despues_bs)}</TableCell>
                        <TableCell>{a.razonpago}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Quitar de seleccion">
                            <IconButton size="small" color="error" onClick={() => quitarFactura(a.cod_factura)}>
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
          <Button size="small" variant="contained" color="success" startIcon={<SaveIcon />} disableElevation onClick={guardar} disabled={saving}>
            {saving ? "Guardando..." : "Guardar emparejamiento"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default PagoMasivoPage;
